import { formatMessage, reconciliationResultMessages } from "@/services/messages";
import type { RunListItem } from "@efes/shared";
import { CheckCircle2, ExternalLink, XCircle } from "lucide-react";
import { DownloadPdfButton } from "../result/DownloadPdfButton";
import { formatDuration } from "../result/ResultBanner";

/**
 * Блок «Последний прогон» на странице деталей набора (FR-007, FR-031).
 *
 * Рендерится для completed/failed-прогонов. Активный прогон (pending/running)
 * рендерится через `RunProgressBar`, не через этот компонент.
 *
 * - Для `completed` → CTA «Открыть результаты», severity-summary, дата завершения.
 * - Для `failed` → причина из БД (короткая) + CTA «Попробовать снова» (US3 расширит
 *   до полного retry-flow).
 */
export interface LastRunPanelProps {
	run: RunListItem;
	onOpenResults?: () => void;
	onRetry?: () => void;
}

function formatDateTime(value: string | null): string {
	if (!value) return "—";
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return value;
	return parsed.toLocaleString("ru-RU", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export function LastRunPanel({ run, onOpenResults, onRetry }: LastRunPanelProps) {
	const messages = reconciliationResultMessages.last_run;
	const isFailed = run.status === "failed";

	return (
		<section className="rounded-lg border border-slate-200 bg-white p-4">
			<header className="mb-3 flex items-center justify-between gap-3">
				<div className="flex items-center gap-2">
					{isFailed ? (
						<XCircle className="h-4 w-4 text-red-500" />
					) : (
						<CheckCircle2 className="h-4 w-4 text-emerald-500" />
					)}
					<h2 className="text-sm font-semibold text-slate-900">{messages.label}</h2>
				</div>
				<div className="flex items-center gap-2">
					{!isFailed && <DownloadPdfButton runId={run.id} runStatus={run.status} />}
					{isFailed
						? onRetry && (
								<button
									type="button"
									onClick={onRetry}
									className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
								>
									{messages.failed_retry}
								</button>
							)
						: onOpenResults && (
								<button
									type="button"
									onClick={onOpenResults}
									className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
								>
									<ExternalLink className="h-3 w-3" aria-hidden="true" />
									{messages.open_results_cta}
								</button>
							)}
				</div>
			</header>

			<dl className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-3">
				<div>
					<dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
						{formatMessage(messages.completed_at_template, {
							datetime: formatDateTime(run.completedAt),
						})}
					</dt>
				</div>
				<div>
					<dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
						{messages.duration_label}
					</dt>
					<dd className="mt-0.5 text-slate-900">
						{formatDuration(run.startedAt, run.completedAt)}
					</dd>
				</div>
				{!isFailed && run.severityBreakdown && (
					<div>
						<dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
							{reconciliationResultMessages.banner.totals_label}
						</dt>
						<dd className="mt-0.5 text-slate-900">
							{formatMessage(reconciliationResultMessages.banner.totals_template, {
								total: run.discrepancyCount ?? 0,
								error: run.severityBreakdown.error,
								warning: run.severityBreakdown.warning,
							})}
						</dd>
					</div>
				)}
			</dl>

			{isFailed && (
				<p className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
					{messages.failed_title}
				</p>
			)}
		</section>
	);
}
